import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { isDemoMode } from '../utils/demo';
import { ServiceFactory } from '../services/factory/ServiceFactory';

/**
 * Permission Context
 * Manages user permissions loaded from backend or demo data
 */

interface PermissionContextType {
  permissions: Set<string>;
  permissionsByResource: Record<string, string[]>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  canAccessResource: (resource: string, action: string) => boolean;
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user, isAuthenticated, getIdToken } = useAuth();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [permissionsByResource, setPermissionsByResource] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch permissions from backend or use demo data
  const fetchPermissions = async () => {
    if (!isAuthenticated || !user) {
      setPermissions(new Set());
      setPermissionsByResource({});
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // In demo mode, use mock permissions
      if (isDemoMode()) {
        const mockPermissions = getDemoPermissions(user.role);
        setPermissions(new Set(mockPermissions));
        setPermissionsByResource(groupPermissionsByResource(mockPermissions));
        setLoading(false);
        return;
      }

      // In production, fetch from API
      const apiService = ServiceFactory.createApiService();
      const token = await getIdToken();

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/permissions/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const data = await response.json();

      if (data.success && data.data) {
        const perms = new Set(data.data.permissions as string[] || []);
        setPermissions(perms);
        setPermissionsByResource(data.data.permissionsByResource || {});
        console.log(`Loaded ${perms.size} permissions for user ${user.username}`);
      } else {
        throw new Error('Invalid permissions response');
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Fallback to demo permissions on error
      if (user) {
        const fallbackPermissions = getDemoPermissions(user.role);
        setPermissions(new Set(fallbackPermissions));
        setPermissionsByResource(groupPermissionsByResource(fallbackPermissions));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions when user or auth state changes
  useEffect(() => {
    fetchPermissions();
  }, [isAuthenticated, user?.id]);

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return permissions.has(permission);
  };

  // Check if user has any of the specified permissions (OR logic)
  const hasAnyPermission = (...perms: string[]): boolean => {
    return perms.some(p => permissions.has(p));
  };

  // Check if user has all of the specified permissions (AND logic)
  const hasAllPermissions = (...perms: string[]): boolean => {
    return perms.every(p => permissions.has(p));
  };

  // Check if user can access a resource with a specific action
  const canAccessResource = (resource: string, action: string): boolean => {
    const permissionName = `${resource}.${action}`;
    return permissions.has(permissionName);
  };

  const value: PermissionContextType = {
    permissions,
    permissionsByResource,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessResource,
    loading,
    error,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Hook to use permission context
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

// Demo mode permissions mapping
function getDemoPermissions(role: string): string[] {
  const rolePermissionMap: Record<string, string[]> = {
    admin: [
      // Admin has all permissions (Kyndly Admin)
      'employee.list', 'employee.view', 'employee.create', 'employee.edit', 'employee.delete', 'employee.export',
      'employer.list', 'employer.view', 'employer.create', 'employer.edit', 'employer.delete', 'employer.export',
      'quote.list', 'quote.view', 'quote.create', 'quote.edit', 'quote.delete', 'quote.share', 'quote.submit', 'quote.export',
      'broker.list', 'broker.view', 'broker.create', 'broker.edit', 'broker.delete', 'broker.assign',
      'user.list', 'user.view', 'user.create', 'user.edit', 'user.delete', 'user.invite', 'user.permissions_view', 'user.permissions_edit',
      'document.view_all', 'document.view_employee', 'document.view_employer', 'document.view_own', 'document.upload', 'document.delete', 'document.share',
      'pepm.view_all', 'pepm.view_own', 'pepm.set_global', 'pepm.set_tpa', 'pepm.set_ga', 'pepm.set_broker', 'pepm.set_employer',
      'analytics.basic.view', 'analytics.advanced.view', 'analytics.export',
      'system.admin', 'system.settings_view', 'system.settings_edit',
      'knowledge.manage', 'knowledge.access_tpa', 'knowledge.access_broker', 'knowledge.access_employer', 'knowledge.access_employee',
      'proposal.view', 'proposal.create', 'proposal.edit', 'proposal.approve', 'proposal.share',
      'kyndchoice.admin', 'kyndchoice.use_self', 'kyndchoice.use_behalf',
      'navigator.search_employee', 'navigator.enroll_behalf',
      'enrollment.dashboard_view', 'enrollment.report_view',
      'report.compliance_view', 'report.compliance_export',
      'lifeevent.record', 'lifeevent.approve',
      'census.upload', 'census.download', 'census.analyze',
      'commission.view', 'commission.export',
      'webhooks.manage', 'api.keys_manage', 'feature.toggle',
      'audit.view_own', 'audit.view_all'
    ],
    tpa_admin: [
      // TPA Admin Extended
      'employee.list', 'employee.view', 'employee.create', 'employee.edit', 'employee.export',
      'employer.list', 'employer.view', 'employer.create', 'employer.edit', 'employer.contact_view', 'employer.contact_edit', 'employer.export',
      'quote.list', 'quote.view', 'quote.create', 'quote.edit', 'quote.share', 'quote.submit', 'quote.export',
      'broker.list', 'broker.view', 'broker.assign',
      'user.list', 'user.view', 'user.create', 'user.edit', 'user.invite',
      'document.view_employee', 'document.view_employer', 'document.upload', 'document.share',
      'pepm.view_all', 'pepm.set_tpa',
      'proposal.view', 'proposal.create', 'proposal.edit',
      'knowledge.access_tpa',
      'enrollment.dashboard_view', 'enrollment.report_view',
      'report.compliance_view', 'report.compliance_export',
      'census.upload', 'census.download'
    ],
    tpa_user: [
      // TPA User Extended
      'employee.list', 'employee.view',
      'employer.list', 'employer.view',
      'quote.list', 'quote.view', 'quote.create',
      'document.view_employee', 'document.view_employer', 'document.upload',
      'knowledge.access_tpa',
      'report.compliance_view'
    ],
    broker: [
      // Broker Agent Extended
      'employee.list', 'employee.view',
      'employer.list', 'employer.view', 'employer.create', 'employer.edit', 'employer.contact_view',
      'quote.list', 'quote.view', 'quote.create', 'quote.edit', 'quote.share', 'quote.submit', 'quote.export',
      'broker.list', 'broker.view', 'broker.assign',
      'document.view_employee', 'document.view_employer', 'document.upload',
      'proposal.view',
      'knowledge.access_broker',
      'enrollment.dashboard_view',
      'report.compliance_view'
    ],
    employer: [
      // Employer Admin Extended
      'employee.list', 'employee.view', 'employee.create', 'employee.edit',
      'document.view_own', 'document.view_employee', 'document.upload',
      'profile.view_own', 'profile.edit_own',
      'dependent.view_own', 'dependent.manage_own',
      'plan.view_own', 'plan.select_own',
      'reimbursement.upload',
      'knowledge.access_employer',
      'lifeevent.record',
      'census.upload'
    ]
  };

  return rolePermissionMap[role] || rolePermissionMap['employer'] || [];
}

// Group permissions by resource for easier checking
function groupPermissionsByResource(permissions: string[]): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  permissions.forEach(permission => {
    const [resource, action] = permission.split('.');
    if (resource && action) {
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(action);
    }
  });

  return grouped;
}
